<script>
import { getFocusableElements } from '@primeuix/utils/dom';
import { mergeProps, normalizeClass, normalizeStyle } from 'vue';
import { resolveFieldData } from '@primeuix/utils/object';
import { FilterService } from '@primevue/core/api';
import Chip from 'primevue/chip';
import PrimeVueAutoComplete from 'primevue/autocomplete';
import Tooltip from 'primevue/tooltip';
import { createDialogBodyOverlayScrollCoordinator } from './dialogOverlayPositioning.js';
import { createFieldFocusBoundary } from './fieldFocusBoundary.js';
import TouchSelect from './touchSelect.js';
import { countPickerOptions, touchPickerPolicyMixin } from './touchPickerPolicy.js';
import { createTouchPickerOptionSizer } from './touchPickerOptionSizing.js';
import { alignPickerOverlay } from './pickerOverlayLayout.js';

const passThroughValue = (value, options) => {
  const resolved = typeof value === 'function' ? value(options) : value;
  return typeof resolved === 'string' || Array.isArray(resolved)
    ? { class: resolved }
    : resolved || {};
};

const fixedGroupSource = Symbol('fixedGroupSource');
const fixedGroupChildren = Symbol('fixedGroupChildren');

const PrimeAutoComplete = {
  name: PrimeVueAutoComplete.name,

  extends: PrimeVueAutoComplete,

  emits: ['query-change'],

  props: {
    options: { type: Array, default: null },
    optionValue: { type: [String, Function], default: null },
    focusBoundaryActive: { type: Boolean, default: false },
    focusBoundaryElements: { type: Function, default: null },
    deferInputChange: { type: Boolean, default: false },
    keepOpenOnSelect: {
      type: Function,
      default: null,
    },
    singleResultTabHandler: {
      type: Function,
      default: null,
    },
    shiftTabSelect: { type: Function, default: null },
  },

  data() {
    return {
      fixedOptionQuery: null,
      pendingInputChange: null,
      lastSelectedOption: undefined,
      headerKeyboardActive: false,
      dialogBodyOverlayScrollCoordinator: createDialogBodyOverlayScrollCoordinator(),
      overlayPointerElement: null,
      fieldFocusBoundary: createFieldFocusBoundary({
        elements: () => [
          this.$el?.closest('.bayhive-autocomplete-shell') || this.$refs.container,
          this.overlayElement(),
          ...(this.focusBoundaryElements?.() || []),
        ],
        active: () => this.focusBoundaryActive,
        onLeave: (event, isCurrent) => this.commitFormFieldBlur(event, isCurrent),
      }),
      pendingHeaderKeyboardActive: false,
    };
  },

  watch: {
    fixedOptionsSelection: {
      immediate: true,
      handler(option) {
        if (option !== undefined) this.lastSelectedOption = option;
      },
    },
    d_value() {
      if (this.fixedOptionsMode) this.fixedOptionQuery = null;
    },
    focusBoundaryActive() {
      this.fieldFocusBoundary.schedule();
    },
  },

  beforeUnmount() {
    if (this.fixedOptionsMode) window.clearTimeout(this.searchTimeout);
    this.unbindDialogBodyOverlayScrollCoordinator();
    this.fieldFocusBoundary.dispose();
    this.unbindOverlayPointerTracking();
  },

  computed: {
    fixedOptionsMode() {
      return Array.isArray(this.options) && !this.multiple;
    },

    fixedOptionList() {
      if (!this.fixedOptionsMode) return [];
      return this.optionGroupLabel
        ? this.flatOptions(this.options).filter((option) => !this.isOptionGroup(option))
        : this.options;
    },

    fixedOptionsSelection() {
      return this.fixedOptionList.find((option) => this.isSelected(option));
    },

    selectedFixedOption() {
      if (this.fixedOptionsSelection !== undefined) return this.fixedOptionsSelection;
      if (this.lastSelectedOption !== undefined && this.isSelected(this.lastSelectedOption)) {
        return this.lastSelectedOption;
      }
      return !this.optionValue && this.d_value !== null && typeof this.d_value === 'object'
        ? this.d_value
        : undefined;
    },

    inputValue() {
      if (!this.fixedOptionsMode) {
        return PrimeVueAutoComplete.computed.inputValue.call(this);
      }
      if (this.fixedOptionQuery !== null) return this.fixedOptionQuery;
      return this.selectedFixedOption === undefined
        ? ''
        : String(this.getOptionLabel(this.selectedFixedOption) ?? '');
    },

    equalityKey() {
      return this.fixedOptionsMode && this.optionValue ? null : this.dataKey;
    },

    focusedOptionId() {
      if (this.headerKeyboardActive) {
        return this.headerFocusTarget()?.id || null;
      }

      return this.focusedOptionIndex !== -1 ? `${this.$id}_${this.focusedOptionIndex}` : null;
    },
  },

  methods: {
    getOptionValue(option) {
      return this.fixedOptionsMode && this.optionValue
        ? resolveFieldData(option, this.optionValue)
        : PrimeVueAutoComplete.methods.getOptionValue.call(this, option);
    },

    isOptionMatched(option, value) {
      if (!this.fixedOptionsMode) {
        return PrimeVueAutoComplete.methods.isOptionMatched.call(this, option, value);
      }
      return (
        this.isValidOption(option) &&
        String(this.getOptionLabel(option) ?? '').toLocaleLowerCase(this.searchLocale) ===
          String(value).toLocaleLowerCase(this.searchLocale)
      );
    },

    onInput(event) {
      this.pendingInputChange = null;
      this.$emit('query-change', { originalEvent: event, query: event.target.value });
      if (!this.fixedOptionsMode) {
        return PrimeVueAutoComplete.methods.onInput.call(this, event);
      }
      // Search text is not a selected value. In particular, it must never reach
      // a scalar FormField or a navigation/autosave change handler.
      window.clearTimeout(this.searchTimeout);
      this.fixedOptionQuery = event.target.value;
      this.focusedOptionIndex = -1;
      this.searchTimeout = window.setTimeout(() => {
        this.search(event, this.fixedOptionQuery ?? '', 'dropdown');
      }, this.delay);
    },

    onChange(event) {
      if (!this.fixedOptionsMode) {
        if (this.deferInputChange && this.forceSelection && !this.multiple) {
          // A composite lookup can still be editing another control. Keep its
          // search text until the complete field commits its pending input.
          this.pendingInputChange = event;
          return;
        }
        return PrimeVueAutoComplete.methods.onChange.call(this, event);
      }
      // Native change also fires when entering the dropdown. Resolve typed
      // values only after the input and its entire overlay have lost focus.
    },

    commitPendingInput() {
      const event = this.pendingInputChange;
      this.pendingInputChange = null;
      if (event) PrimeVueAutoComplete.methods.onChange.call(this, event);
    },

    search(event, query, source) {
      if (!this.fixedOptionsMode) {
        return PrimeVueAutoComplete.methods.search.call(this, event, query, source);
      }
      if (query === null || query === undefined) return;
      // The options wrapper resolves completion explicitly, including repeat
      // searches whose suggestions keep the same reference and empty results.
      this.$emit('complete', { originalEvent: event, query });
    },

    commitFixedOptionInput(event) {
      if (!this.fixedOptionsMode) return;
      window.clearTimeout(this.searchTimeout);
      this.searching = false;
      if (this.fixedOptionQuery === null) return;

      const query = this.fixedOptionQuery.trim();
      const option = this.fixedOptionList.find((candidate) =>
        this.isOptionMatched(candidate, query),
      );
      this.fixedOptionQuery = null;

      if (option !== undefined) {
        if (!this.isSelected(option)) {
          PrimeVueAutoComplete.methods.onOptionSelect.call(this, event, option, false);
        }
      } else if (
        query === '' &&
        this.showClear &&
        this.d_value !== null &&
        this.d_value !== undefined
      ) {
        this.updateModel(event, null);
        this.$emit('clear');
      }
      // An unmatched query restores the committed selection. Options may be
      // refreshed asynchronously, so an absent result must not erase a value.
    },

    onClearClick(event) {
      this.pendingInputChange = null;
      this.finishFixedOptionQuery();
      return PrimeVueAutoComplete.methods.onClearClick.call(this, event);
    },

    finishFixedOptionQuery() {
      if (!this.fixedOptionsMode) return;
      window.clearTimeout(this.searchTimeout);
      this.searching = false;
      this.fixedOptionQuery = null;
    },

    inputElement() {
      return this.multiple ? this.$refs.focusInput : this.$refs.focusInput?.$el;
    },

    overlayElement() {
      return this.overlay || document.getElementById(this.panelId);
    },

    alignOverlay() {
      const own = this.$refs.container;
      const parent = own?.parentElement;
      const control = parent?.dataset?.pcName === 'inputtags' ? parent : own;
      return alignPickerOverlay(this.overlay, control, () =>
        PrimeVueAutoComplete.methods.alignOverlay.call(this),
      );
    },

    bindDialogBodyOverlayScrollCoordinator() {
      this.dialogBodyOverlayScrollCoordinator.bind({
        alignOverlay: () => this.alignOverlay(),
        appendTo: this.appendTo,
        control: this.$refs.container,
        overlay: this.overlayElement(),
        isVisible: () => this.overlayVisible,
      });
    },

    unbindDialogBodyOverlayScrollCoordinator() {
      this.dialogBodyOverlayScrollCoordinator?.unbind();
    },

    headerFocusTarget() {
      return (
        this.overlayElement()?.querySelector(
          '.bayhive-field-overlay-option-row--header input:not([disabled]), ' +
            '.bayhive-field-overlay-option-row--header button:not([disabled]), ' +
            '.bayhive-field-overlay-option-row--header [tabindex]:not([tabindex="-1"])',
        ) || null
      );
    },

    setHeaderKeyboardActive(active) {
      this.headerKeyboardActive = Boolean(active);

      const overlay = this.overlayElement();
      const input = this.inputElement();
      const headerId = this.headerFocusTarget()?.id;

      overlay?.classList.toggle(
        'bayhive-autocomplete-overlay--header-active',
        this.headerKeyboardActive,
      );

      if (this.headerKeyboardActive) {
        this.focusedOptionIndex = -1;
      }

      if (input && headerId) {
        if (this.headerKeyboardActive) {
          input.setAttribute('aria-activedescendant', headerId);
        } else if (input.getAttribute('aria-activedescendant') === headerId) {
          input.removeAttribute('aria-activedescendant');
        }
      }
    },

    openOverlayAtHeader(event) {
      this.pendingHeaderKeyboardActive = true;
      this.search(event, this.fixedOptionsMode ? '' : event.target?.value || '', 'dropdown');
      event.preventDefault();
    },

    bindOverlayPointerTracking() {
      const overlay = this.overlayElement();

      if (!overlay || overlay === this.overlayPointerElement) {
        return;
      }

      this.unbindOverlayPointerTracking();
      this.overlayPointerElement = overlay;
      this.overlayPointerElement.addEventListener('pointermove', this.onOverlayPointerMove);
    },

    commitFormFieldBlur(event, isCurrent) {
      this.commitPendingInput();
      this.commitFixedOptionInput(event);
      this.dirty = false;
      this.focused = false;
      this.focusedOptionIndex = -1;
      this.$emit('blur', event);

      this.$nextTick(() => {
        if (!isCurrent()) return;
        this.formField.onChange?.({
          originalEvent: event,
          value: this.d_value,
        });

        this.$nextTick(() => {
          if (isCurrent()) this.formField.onBlur?.(event);
        });
      });
    },

    unbindOverlayPointerTracking() {
      this.overlayPointerElement?.removeEventListener('pointermove', this.onOverlayPointerMove);
      this.overlayPointerElement = null;
    },

    onOverlayPointerMove(event) {
      if (
        !event.target?.closest?.(
          '.bayhive-field-overlay-option-row--header, .p-autocomplete-option',
        )
      ) {
        return;
      }

      this.setHeaderKeyboardActive(false);
      this.focusedOptionIndex = -1;
    },

    show(isFocus) {
      const keyboardOpen = this.pendingHeaderKeyboardActive;
      const result = PrimeVueAutoComplete.methods.show.call(this, isFocus);

      this.$nextTick(() => {
        this.bindOverlayPointerTracking();
        const headerActive = keyboardOpen && Boolean(this.headerFocusTarget());
        this.setHeaderKeyboardActive(headerActive);
        this.focusedOptionIndex = keyboardOpen && !headerActive ? this.findFirstOptionIndex() : -1;
        this.pendingHeaderKeyboardActive = false;
      });

      return result;
    },

    onOverlayAfterEnter() {
      const result = PrimeVueAutoComplete.methods.onOverlayAfterEnter.call(this);

      this.bindDialogBodyOverlayScrollCoordinator();

      return result;
    },

    onOverlayLeave(el) {
      this.fieldFocusBoundary.schedule();
      this.unbindDialogBodyOverlayScrollCoordinator();

      return PrimeVueAutoComplete.methods.onOverlayLeave.call(this, el);
    },

    onOverlayAfterLeave(el) {
      const result = PrimeVueAutoComplete.methods.onOverlayAfterLeave.call(this, el);

      this.fieldFocusBoundary.schedule();

      return result;
    },

    onFocus(event) {
      this.fieldFocusBoundary.focus();

      return PrimeVueAutoComplete.methods.onFocus.call(this, event);
    },

    hide(isFocus) {
      this.pendingHeaderKeyboardActive = false;
      this.setHeaderKeyboardActive(false);

      return PrimeVueAutoComplete.methods.hide.call(this, isFocus);
    },

    onArrowDownKey(event) {
      if (!this.overlayVisible) {
        this.openOverlayAtHeader(event);
        return;
      }

      const firstOptionIndex = this.findFirstOptionIndex();

      if (this.headerKeyboardActive) {
        this.setHeaderKeyboardActive(false);
        this.changeFocusedOptionIndex(event, firstOptionIndex);
        event.preventDefault();
        return;
      }

      if (this.focusedOptionIndex === -1 && this.headerFocusTarget()) {
        this.setHeaderKeyboardActive(true);
        event.preventDefault();
        return;
      }

      this.changeFocusedOptionIndex(event, this.findNextOptionIndex(this.focusedOptionIndex));
      event.preventDefault();
    },

    onArrowUpKey(event) {
      if (!this.overlayVisible) {
        this.openOverlayAtHeader(event);
        return;
      }

      if (!this.headerFocusTarget()) {
        return PrimeVueAutoComplete.methods.onArrowUpKey.call(this, event);
      }

      const firstOptionIndex = this.findFirstOptionIndex();

      if (
        this.headerKeyboardActive ||
        this.focusedOptionIndex === -1 ||
        this.focusedOptionIndex === firstOptionIndex
      ) {
        this.setHeaderKeyboardActive(true);
        event.preventDefault();
        return;
      }

      this.changeFocusedOptionIndex(event, this.findPrevOptionIndex(this.focusedOptionIndex));
      event.preventDefault();
    },

    onEnterKey(event) {
      if (this.headerKeyboardActive) {
        event.preventDefault();
        event.stopPropagation();
        this.headerFocusTarget()?.click?.();
        return;
      }

      const option = this.visibleOptions[this.focusedOptionIndex];
      if (
        this.overlayVisible &&
        this.isValidOption(option) &&
        this.keepOpenOnSelect?.(option) === true
      ) {
        // PrimeVue's Enter handler closes after selection even when the option
        // keeps its overlay open. Navigation choices need to stay in the list.
        this.onOptionSelect(event, option, false);
        this.$nextTick(() => {
          if (!this.overlayVisible || this.inputElement() !== document.activeElement) return;
          const index = this.visibleOptions.findIndex(
            (candidate) =>
              this.isValidOption(candidate) &&
              this.isEquals(this.getOptionValue(candidate), this.getOptionValue(option)),
          );
          this.focusedOptionIndex = index;
          if (index !== -1) this.scrollInView(index);
        });
        event.preventDefault();
        return;
      }

      return PrimeVueAutoComplete.methods.onEnterKey.call(this, event);
    },

    onBlur(event) {
      // Visual focus follows this control; validation may span sibling controls.
      this.focused = false;
      this.fieldFocusBoundary.blur(event);
    },

    onSpaceKey(event) {
      if (this.headerKeyboardActive) {
        this.onEnterKey(event);
        return;
      }

      return PrimeVueAutoComplete.methods.onSpaceKey.call(this, event);
    },

    onOptionMouseMove() {
      this.setHeaderKeyboardActive(false);
      this.focusedOptionIndex = -1;
    },

    onOptionSelect(event, option, shouldHide = true) {
      this.pendingInputChange = null;
      this.finishFixedOptionQuery();
      this.setHeaderKeyboardActive(false);
      const keepOpen = this.keepOpenOnSelect?.(option) === true;
      if (keepOpen) this.focusedOptionIndex = -1;

      return PrimeVueAutoComplete.methods.onOptionSelect.call(
        this,
        event,
        option,
        this.multiple || keepOpen ? false : shouldHide,
      );
    },

    selectOptionAndClose(event, option) {
      this.pendingInputChange = null;
      this.finishFixedOptionQuery();
      if (this.keepOpenOnSelect?.(option) === true) {
        return this.onOptionSelect(event, option, false);
      }
      this.setHeaderKeyboardActive(false);

      PrimeVueAutoComplete.methods.onOptionSelect.call(this, event, option, false);
      return this.hide(false);
    },

    singleSelectableSuggestion() {
      const selectableSuggestions = (this.visibleOptions || []).filter((option) =>
        this.isValidOption(option),
      );

      return selectableSuggestions.length === 1 ? selectableSuggestions[0] : null;
    },

    onTabKey(event) {
      if (event.shiftKey && this.shiftTabSelect?.(event)) return;
      const suggestion = this.singleSelectableSuggestion();

      if (
        !event.shiftKey &&
        this.overlayVisible &&
        suggestion !== null &&
        this.singleResultTabHandler?.(event, suggestion)
      ) {
        return;
      }

      if (this.fixedOptionsMode) {
        const option = this.visibleOptions[this.focusedOptionIndex];
        if (this.overlayVisible && this.isValidOption(option)) {
          this.onOptionSelect(event, option, false);
        }
        // Native Tab owns the next focus target. Selecting must not schedule
        // PrimeVue's deferred return-focus after the browser has moved on.
        if (this.overlayVisible) this.hide(false);
        return;
      }

      return PrimeVueAutoComplete.methods.onTabKey.call(this, event);
    },

    onEscapeKey(event) {
      this.finishFixedOptionQuery();
      if (this.overlayVisible) this.hide(false);
      event.preventDefault();
    },

    onOverlayKeyDown(event) {
      if (event.code === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.onEscapeKey(event);

        this.$nextTick(() => {
          const input = this.multiple ? this.$refs.focusInput : this.$refs.focusInput?.$el;
          input?.blur?.();
        });

        return;
      }

      if (
        (event.code === 'ArrowDown' || event.code === 'ArrowUp') &&
        event.target?.closest?.('.bayhive-field-overlay-option-row--header')
      ) {
        event.preventDefault();
        event.stopImmediatePropagation();

        if (event.code === 'ArrowDown') {
          const firstOptionIndex = this.findFirstOptionIndex();

          this.inputElement()?.focus?.({ preventScroll: true });
          this.$nextTick(() => {
            this.setHeaderKeyboardActive(false);
            this.changeFocusedOptionIndex(event, firstOptionIndex);
          });
        }

        return;
      }

      if (event.code === 'Tab' && event.shiftKey) {
        event.preventDefault();
        event.stopImmediatePropagation();

        const input = this.multiple ? this.$refs.focusInput : this.$refs.focusInput?.$el;
        input?.focus?.({ preventScroll: true });

        return;
      }

      return PrimeVueAutoComplete.methods.onOverlayKeyDown.call(this, event);
    },
  },
};

export default {
  name: 'ShopManagementAutoComplete',

  inheritAttrs: false,

  mixins: [touchPickerPolicyMixin],

  components: {
    Chip,
    PrimeAutoComplete,
    TouchSelect,
  },

  directives: {
    tooltip: Tooltip,
  },

  emits: [
    'before-hide',
    'blur',
    'complete',
    'filter',
    'focus',
    'hide',
    'keydown',
    'query-change',
    'show',
  ],

  props: {
    // Supplying options opts a single-value picker into local search while
    // retaining its committed scalar/object model. Suggestions stay unchanged.
    options: { type: Array, default: null },
    optionValue: { type: [String, Function], default: null },
    filterFields: { type: Array, default: () => [] },
    filterMatchMode: { type: String, default: 'contains' },
    filterLocale: { type: String, default: undefined },
    remote: { type: Boolean, default: false },
    resetFilterOnHide: { type: Boolean, default: false },
    emptyMessage: { type: String, default: null },
    emptyFilterMessage: { type: String, default: null },
    selectedValueStyle: { type: [String, Object, Array], default: null },
    focusBoundaryActive: { type: Boolean, default: false },
    focusBoundaryElements: { type: Function, default: null },
    deferInputChange: { type: Boolean, default: false },
    // Opt-in for navigation choices that replace the current suggestions.
    keepOpenOnSelect: {
      type: Function,
      default: null,
    },
    collapseSelectionOverflow: {
      type: Boolean,
      default: true,
    },
    dropdown: {
      type: Boolean,
      default: true,
    },
    dropdownIcon: {
      type: String,
      default: 'fa-solid fa-chevron-down p-icon',
    },
    dropdownMode: {
      type: String,
      default: 'blank',
    },
    pt: {
      type: Object,
      default: () => ({}),
    },
    suggestions: {
      type: Array,
      default: () => [],
    },
    singleResultTabSelect: {
      type: Function,
      default: null,
    },
    shiftTabSelect: { type: Function, default: null },
    // An explicit search action when Enter has no highlighted option to select.
    querySubmitHandler: { type: Function, default: null },
    tabToOverlay: {
      type: Boolean,
      default: true,
    },
    virtualScrollerOptions: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      autoCompleteMounted: false,
      touchSearchTimeout: null,
      touchQueryTooShort: false,
      // Search text set from outside (a scan) for the touch picker's next open.
      pendingTouchQuery: null,
      touchOptionSizer: createTouchPickerOptionSizer(),
      fixedOptionsQuery: '',
      hiddenSelectionLabels: [],
      inputFocused: false,
      selectionHasOverflow: false,
      selectionMutationObserver: null,
      selectionOverflowFrame: null,
      selectionOverlayOpen: false,
      selectionResizeObserver: null,
      virtualOptionMinHeight: 0,
    };
  },

  computed: {
    useMobileSelect() {
      const forceSelection = this.$attrs.forceSelection ?? this.$attrs['force-selection'];
      return (
        this.isTouchPickerContext &&
        !this.isMultiple &&
        (this.fixedOptionsMode || forceSelection === true || forceSelection === '')
      );
    },

    touchExternalFilter() {
      return !this.fixedOptionsMode || this.remote;
    },

    touchOptions() {
      if (this.touchQueryTooShort) return [];
      return this.fixedOptionsMode ? this.options : this.suggestions;
    },

    touchPickerOptionCount() {
      return countPickerOptions(
        this.touchOptions,
        this.$attrs.optionGroupChildren ?? this.$attrs['option-group-children'],
      );
    },

    touchSelectAttributes() {
      const attributes = { ...this.$attrs };
      for (const name of [
        'forceSelection',
        'force-selection',
        'inputProps',
        'input-props',
        'inputClass',
        'input-class',
        'inputStyle',
        'input-style',
        'delay',
        'minLength',
        'min-length',
        'emptySearchMessage',
        'empty-search-message',
        'filterInputProps',
        'filter-input-props',
      ])
        delete attributes[name];
      const showClear = this.$attrs.showClear ?? this.$attrs['show-clear'];
      return {
        ...attributes,
        options: this.touchOptions,
        optionValue: this.optionValue,
        externalFilter: this.touchExternalFilter,
        filter: this.touchExternalFilter || this.touchPickerFilter,
        filterFields: this.filterFields.length ? this.filterFields : undefined,
        filterMatchMode: this.filterMatchMode,
        filterLocale: this.filterLocale ?? this.autocompleteSearchLocale,
        filterPlaceholder:
          this.$attrs.filterPlaceholder ?? this.$attrs['filter-placeholder'] ?? 'Search',
        filterIcon:
          this.$attrs.filterIcon ?? this.$attrs['filter-icon'] ?? 'fa-solid fa-magnifying-glass',
        dropdownIcon: this.dropdownIcon,
        loadingIcon: 'fa-solid fa-spinner fa-spin p-icon',
        showClear: showClear === undefined ? !this.fixedOptionsMode : showClear,
        emptyMessage: this.emptyMessage ?? this.autocompleteEmptyMessage,
        emptyFilterMessage: this.emptyFilterMessage ?? this.autocompleteEmptyMessage,
        overlayClass: normalizeClass([
          this.$attrs.overlayClass ?? this.$attrs['overlay-class'],
          this.fieldOverlayClasses,
          'bayhive-touch-picker-overlay',
        ]),
        labelStyle: normalizeStyle(this.selectedValueStyle),
        resetFilterOnHide: this.resetFilterOnHide,
        autoFilterFocus: false,
        focusOnHover: false,
        editable: false,
      };
    },

    touchSelectPassThrough() {
      const inputProps = this.$attrs.inputProps ?? this.$attrs['input-props'] ?? {};
      const filterInputProps =
        this.$attrs.filterInputProps ?? this.$attrs['filter-input-props'] ?? {};
      const required = this.$attrs.required;
      const ariaRequired =
        this.$attrs['aria-required'] ??
        this.$attrs.ariaRequired ??
        (required !== undefined && required !== null && required !== false ? true : undefined);
      return {
        ...this.pt,
        label: (options) => {
          const input = passThroughValue(this.pt.pcInputText, options);
          return mergeProps(
            {
              'aria-required': ariaRequired,
              class: this.$attrs.inputClass ?? this.$attrs['input-class'],
              style: this.$attrs.inputStyle ?? this.$attrs['input-style'],
            },
            inputProps,
            passThroughValue(input.root, options),
            passThroughValue(this.pt.label, options),
          );
        },
        pcFilter: (options) => {
          const filter = passThroughValue(this.pt.pcFilter, options);
          return {
            ...filter,
            root: (rootOptions) =>
              mergeProps(
                { 'aria-label': 'Search' },
                filterInputProps,
                passThroughValue(filter.root, rootOptions),
              ),
          };
        },
      };
    },

    touchSlotNames() {
      return Object.keys(this.$slots).filter(
        (name) => !['emptyfilter', 'clearicon', 'loader', 'loadingicon'].includes(name),
      );
    },

    fixedOptionsMode() {
      return Array.isArray(this.options) && !this.isMultiple;
    },

    autocompleteSuggestions() {
      if (!this.fixedOptionsMode) return this.suggestions;
      const matches = (option) => {
        if (this.remote || !this.fixedOptionsQuery) return true;
        const filter =
          FilterService.filters[this.filterMatchMode] || FilterService.filters.contains;
        const fields = this.filterFields?.length
          ? this.filterFields
          : [this.$attrs.optionLabel ?? this.$attrs['option-label']];
        return fields.some((field) =>
          filter(
            field ? resolveFieldData(option, field) : option,
            this.fixedOptionsQuery,
            this.filterLocale,
          ),
        );
      };
      const groupLabel = this.$attrs.optionGroupLabel ?? this.$attrs['option-group-label'];
      const groupChildren = this.$attrs.optionGroupChildren ?? this.$attrs['option-group-children'];
      if (groupLabel && groupChildren) {
        return this.options
          .map((group) => {
            const children = (resolveFieldData(group, groupChildren) || []).filter(matches);
            return { [fixedGroupSource]: group, [fixedGroupChildren]: children };
          })
          .filter((group) => group[fixedGroupChildren].length);
      }
      return this.options.filter(matches);
    },

    autocompleteOptionGroupLabel() {
      const field = this.$attrs.optionGroupLabel ?? this.$attrs['option-group-label'];
      return this.fixedOptionsMode && field
        ? (group) => resolveFieldData(group[fixedGroupSource] ?? group, field)
        : field;
    },

    autocompleteOptionGroupChildren() {
      const field = this.$attrs.optionGroupChildren ?? this.$attrs['option-group-children'];
      return this.fixedOptionsMode && field
        ? (group) => group[fixedGroupChildren] ?? resolveFieldData(group, field)
        : field;
    },

    autocompleteSearchLocale() {
      return (
        this.$attrs.searchLocale ??
        this.$attrs['search-locale'] ??
        (this.fixedOptionsMode ? this.filterLocale : undefined)
      );
    },

    autocompleteEmptyMessage() {
      if (!this.fixedOptionsMode) {
        return this.$attrs.emptySearchMessage ?? this.$attrs['empty-search-message'];
      }
      return this.fixedOptionsQuery
        ? this.emptyFilterMessage || this.$primevue.config.locale.emptySearchMessage
        : this.emptyMessage || this.$primevue.config.locale.emptyMessage;
    },

    autocompleteVirtualScrollerOptions() {
      const options = this.virtualScrollerOptions;
      if (!Number.isFinite(options?.itemSize) || this.virtualOptionMinHeight <= options.itemSize) {
        return options;
      }

      return { ...options, itemSize: this.virtualOptionMinHeight };
    },

    selectedValue() {
      return this.autoCompleteMounted
        ? this.$refs.autoComplete?.d_value
        : Object.prototype.hasOwnProperty.call(this.$attrs, 'modelValue')
          ? this.$attrs.modelValue
          : this.$attrs['model-value'];
    },

    hasSelectedValueSlot() {
      return (
        !this.useMobileSelect &&
        this.fixedOptionsMode &&
        Boolean(this.$slots.value)
      );
    },

    showSelectedValueSlot() {
      return this.hasSelectedValueSlot && !this.selectionExpanded;
    },

    forwardedSlotNames() {
      return Object.keys(this.$slots).filter((name) => !this.fixedOptionsMode || name !== 'value');
    },

    fieldOverlayClasses() {
      const size = ['small', 'large'].includes(this.$attrs.size) ? this.$attrs.size : 'medium';

      return ['bayhive-field-overlay', `bayhive-field-overlay--${size}`];
    },

    autocompletePassThrough() {
      const chipPassThrough = this.pt.pcChip;
      const dropdownPassThrough = this.pt.dropdown;
      const overlayPassThrough = this.pt.overlay;

      return {
        ...this.pt,
        dropdown: (options) => {
          const resolved =
            typeof dropdownPassThrough === 'function'
              ? dropdownPassThrough(options)
              : dropdownPassThrough;
          const attributes =
            typeof resolved === 'string' || Array.isArray(resolved)
              ? { class: resolved }
              : resolved || {};

          return {
            'aria-label': 'Show suggestions',
            ...attributes,
            tabindex: -1,
          };
        },
        overlay: (options) => {
          const resolvedOverlayPassThrough =
            typeof overlayPassThrough === 'function'
              ? overlayPassThrough(options)
              : overlayPassThrough;
          const overlayAttributes =
            typeof resolvedOverlayPassThrough === 'string' ||
            Array.isArray(resolvedOverlayPassThrough)
              ? { class: resolvedOverlayPassThrough }
              : resolvedOverlayPassThrough || {};

          return {
            ...overlayAttributes,
            class: [this.fieldOverlayClasses, overlayAttributes.class],
          };
        },
        pcChip: (options) => {
          const resolvedChipPassThrough =
            typeof chipPassThrough === 'function'
              ? chipPassThrough(options)
              : chipPassThrough || {};

          return {
            ...resolvedChipPassThrough,
            removeIcon: {
              ...(resolvedChipPassThrough.removeIcon || {}),
              tabindex: -1,
            },
          };
        },
      };
    },

    // $refs is not reactive: without the mounted flag this computed would
    // cache the pre-mount `false` forever and never track the inner overlay.
    overlayVisible() {
      return this.autoCompleteMounted && Boolean(this.$refs.autoComplete?.overlayVisible);
    },

    isMultiple() {
      return (
        this.$attrs.multiple !== undefined &&
        this.$attrs.multiple !== null &&
        this.$attrs.multiple !== false
      );
    },

    isFluid() {
      return (
        this.$attrs.fluid !== undefined && this.$attrs.fluid !== null && this.$attrs.fluid !== false
      );
    },

    selectionExpanded() {
      return this.inputFocused || this.selectionOverlayOpen || this.overlayVisible;
    },

    autocompleteClasses() {
      return {
        'bayhive-autocomplete--large': this.$attrs.size === 'large',
        'bayhive-autocomplete--small': this.$attrs.size === 'small',
        'bayhive-autocomplete--selection-overflow':
          this.collapseSelectionOverflow && this.isMultiple,
        'bayhive-autocomplete--selection-expanded': this.selectionExpanded,
        'bayhive-autocomplete--has-selection-overflow': this.selectionHasOverflow,
      };
    },

    autocompleteShellClasses() {
      return {
        'bayhive-autocomplete-shell--fluid': this.isFluid,
        'bayhive-autocomplete-shell--options': this.fixedOptionsMode,
        'bayhive-autocomplete-shell--value-slot': this.hasSelectedValueSlot,
        'bayhive-autocomplete-shell--custom-value': this.showSelectedValueSlot,
      };
    },

    selectionOverflowLabel() {
      return `+${this.hiddenSelectionLabels.length} more`;
    },

    selectionOverflowTooltip() {
      return this.hiddenSelectionLabels.join('\n');
    },

    selectionOverflowAriaLabel() {
      return `${this.hiddenSelectionLabels.length} additional selections: ${this.hiddenSelectionLabels.join(', ')}`;
    },
  },

  watch: {
    useMobileSelect() {
      window.clearTimeout(this.touchSearchTimeout);
      this.endTouchPickerSession();
      this.teardownVirtualOptionSizing();
    },
    virtualScrollerOptions() {
      if (this.selectionOverlayOpen) this.$nextTick(this.setupVirtualOptionSizing);
    },
  },

  mounted() {
    this.autoCompleteMounted = true;
    this.$nextTick(this.setupSelectionOverflowTracking);
  },

  beforeUnmount() {
    window.clearTimeout(this.touchSearchTimeout);
    this.teardownSelectionOverflowTracking();
    this.teardownVirtualOptionSizing();
  },

  methods: {
    commitPendingInput() {
      this.$refs.autoComplete?.commitPendingInput?.();
    },

    handleTouchBeforeShow() {
      this.beginTouchPickerSession();
      this.touchQueryTooShort = false;
      if (this.pendingTouchQuery !== null) {
        // The suggestions for this text are already loaded: keep them, and show the text.
        const picker = this.$refs.autoComplete;
        if (picker) picker.filterValue = this.pendingTouchQuery;
        this.fixedOptionsQuery = this.pendingTouchQuery;
        this.pendingTouchQuery = null;
        return;
      }
      if (!this.touchExternalFilter) return;
      const picker = this.$refs.autoComplete;
      const query =
        this.dropdownMode === 'current'
          ? String(
              picker?.selectedOption === undefined
                ? ''
                : (picker.getOptionLabel(picker.selectedOption) ?? ''),
            )
          : '';
      if (picker) picker.filterValue = query;
      // Select(options, remote) already exposes @show for its initial load.
      // Suggestion-based autocomplete instead requests its default suggestions.
      if (!this.fixedOptionsMode || this.$.vnode.props?.onComplete) {
        this.$emit('complete', { originalEvent: null, query });
      }
    },

    handleTouchFilter(event) {
      const query = event.value || '';
      this.$emit('query-change', { originalEvent: event.originalEvent, query });
      this.fixedOptionsQuery = query;
      window.clearTimeout(this.touchSearchTimeout);
      const minimum = Number(this.$attrs.minLength ?? this.$attrs['min-length'] ?? 1);
      this.touchQueryTooShort = !this.fixedOptionsMode && Boolean(query) && query.length < minimum;
      if (this.touchQueryTooShort) return;
      const complete = () => {
        this.touchSearchTimeout = null;
        this.$emit('filter', event);
        this.$emit('complete', { originalEvent: event.originalEvent, query });
      };
      if (this.touchExternalFilter) {
        const delay = Number(this.$attrs.delay ?? 300);
        this.touchSearchTimeout = window.setTimeout(complete, Math.max(0, delay));
      } else {
        complete();
      }
      this.resetOverlayScrollToStart();
    },

    forwardedSlotProps(name, slotProps) {
      if (
        this.fixedOptionsMode &&
        name === 'optiongroup' &&
        slotProps?.option?.[fixedGroupSource]
      ) {
        return { ...slotProps, option: slotProps.option[fixedGroupSource] };
      }
      return slotProps || {};
    },

    inputElement() {
      return this.$refs.autoComplete?.$el?.querySelector('[role="combobox"]') || null;
    },

    isInputFocused() {
      return this.inputElement() === document.activeElement;
    },

    async completeFocusedInput(originalEvent = null) {
      const input = this.inputElement();

      if (!input || input !== document.activeElement) {
        return;
      }

      const event = {
        originalEvent,
        query: input.value || '',
      };
      if (this.fixedOptionsMode) {
        window.clearTimeout(this.$refs.autoComplete?.searchTimeout);
        this.handleComplete(event);
      } else {
        this.$emit('complete', event);
      }

      await this.$nextTick();
      this.showForFocusedInput();
    },

    handleBeforeHide() {
      if (this.useMobileSelect) window.clearTimeout(this.touchSearchTimeout);
      this.$emit('before-hide');
    },

    handleComplete(event) {
      if (this.fixedOptionsMode) {
        this.fixedOptionsQuery = event.query || '';
        this.$emit('filter', { originalEvent: event.originalEvent, value: this.fixedOptionsQuery });
        this.$nextTick(() => this.showForFocusedInput());
      }
      this.$emit('complete', event);
      this.resetOverlayScrollToStart();
    },

    handleFocus(event) {
      this.inputFocused = true;
      this.scheduleSelectionOverflowSync();
      this.$emit('focus', event);
    },

    handleHide() {
      this.endTouchPickerSession();
      this.selectionOverlayOpen = false;
      this.teardownVirtualOptionSizing();
      if (this.fixedOptionsMode && this.resetFilterOnHide) this.fixedOptionsQuery = '';
      this.scheduleSelectionOverflowSync();
      this.$emit('hide');
    },

    handleKeydown(event) {
      this.$emit('keydown', event);

      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
        return;
      }

      const autocomplete = this.$refs.autoComplete;

      if (event.key === 'Escape' || event.code === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();

        this.$nextTick(() => {
          this.inputElement()?.blur?.();
        });

        return;
      }

      if (event.key !== 'Enter') {
        return;
      }

      if (autocomplete?.focusedOptionIndex !== -1) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      this.completeFocusedInput(event);
    },

    handleShow() {
      this.selectionOverlayOpen = true;
      this.$nextTick(this.setupVirtualOptionSizing);
      this.scheduleSelectionOverflowSync();
      this.resetOverlayScrollToStart();
      this.$emit('show');
    },

    handleBlur(event) {
      this.inputFocused = false;
      this.scheduleSelectionOverflowSync();
      this.$emit('blur', event);
    },

    setupVirtualOptionSizing() {
      this.teardownVirtualOptionSizing();
      if (!this.selectionOverlayOpen || !Number.isFinite(this.virtualScrollerOptions?.itemSize))
        return;

      const overlay = this.overlayElement();
      if (!overlay) return;

      this.touchOptionSizer.bind({
        overlay,
        onSize: (height) => {
          this.virtualOptionMinHeight = height;
        },
      });
    },

    teardownVirtualOptionSizing() {
      this.touchOptionSizer.unbind();
      this.virtualOptionMinHeight = 0;
    },

    setupSelectionOverflowTracking() {
      if (!this.collapseSelectionOverflow || !this.isMultiple) {
        return;
      }

      const autocomplete = this.$refs.autoComplete?.$el;
      const selection = autocomplete?.querySelector('.p-autocomplete-input-multiple');

      if (!autocomplete || !selection) {
        return;
      }

      this.teardownSelectionOverflowTracking();

      if (typeof ResizeObserver !== 'undefined') {
        this.selectionResizeObserver = new ResizeObserver(this.scheduleSelectionOverflowSync);
        this.selectionResizeObserver.observe(autocomplete);
      }

      if (typeof MutationObserver !== 'undefined') {
        this.selectionMutationObserver = new MutationObserver(this.scheduleSelectionOverflowSync);
        this.selectionMutationObserver.observe(selection, {
          childList: true,
        });
      }

      this.scheduleSelectionOverflowSync();
    },

    teardownSelectionOverflowTracking() {
      if (this.selectionOverflowFrame !== null) {
        window.cancelAnimationFrame(this.selectionOverflowFrame);
        this.selectionOverflowFrame = null;
      }

      this.selectionResizeObserver?.disconnect();
      this.selectionMutationObserver?.disconnect();
      this.selectionResizeObserver = null;
      this.selectionMutationObserver = null;
    },

    scheduleSelectionOverflowSync() {
      if (this.selectionOverflowFrame !== null) {
        return;
      }

      this.selectionOverflowFrame = window.requestAnimationFrame(() => {
        this.selectionOverflowFrame = null;
        this.syncSelectionOverflow();
      });
    },

    selectionOverflowIndexes(chips) {
      const rowOffsets = [];
      const maximumChipRows = this.selectionExpanded ? 1 : 2;

      return chips.reduce((hiddenIndexes, chip, index) => {
        const rowIndex = rowOffsets.findIndex((offset) => Math.abs(offset - chip.offsetTop) <= 1);
        const resolvedRowIndex = rowIndex === -1 ? rowOffsets.push(chip.offsetTop) - 1 : rowIndex;

        if (resolvedRowIndex >= maximumChipRows) {
          hiddenIndexes.push(index);
        }

        return hiddenIndexes;
      }, []);
    },

    syncSelectionOverflow() {
      const autocomplete = this.$refs.autoComplete?.$el;
      const chips = Array.from(autocomplete?.querySelectorAll('.p-autocomplete-chip-item') || []);

      chips.forEach((chip) => {
        chip.hidden = false;
        chip.removeAttribute('aria-hidden');
      });
      autocomplete?.classList.remove('bayhive-autocomplete--has-selection-overflow');

      if (
        !autocomplete ||
        !this.collapseSelectionOverflow ||
        !this.isMultiple ||
        chips.length === 0
      ) {
        this.selectionHasOverflow = false;
        this.hiddenSelectionLabels = [];
        return;
      }

      let hiddenIndexes = this.selectionOverflowIndexes(chips);

      if (hiddenIndexes.length > 0) {
        autocomplete.classList.add('bayhive-autocomplete--has-selection-overflow');
        hiddenIndexes = this.selectionOverflowIndexes(chips);
      }

      const hiddenIndexSet = new Set(hiddenIndexes);
      const hiddenLabels = [];

      chips.forEach((chip, index) => {
        if (!hiddenIndexSet.has(index)) {
          return;
        }

        chip.hidden = true;
        chip.setAttribute('aria-hidden', 'true');
        hiddenLabels.push(
          chip.getAttribute('aria-label') || chip.textContent?.trim() || 'Selection',
        );
      });

      this.selectionHasOverflow = hiddenLabels.length > 0;
      this.hiddenSelectionLabels = hiddenLabels;
    },

    overlayElement() {
      if (this.useMobileSelect) return this.$refs.autoComplete?.overlay || null;
      const overlayId = this.inputElement()?.getAttribute('aria-controls');
      const controlledElement = overlayId ? document.getElementById(overlayId) : null;

      return controlledElement?.closest('.p-autocomplete-overlay') || controlledElement;
    },

    firstOverlayFocusTarget() {
      return (
        this.overlayElement()?.querySelector(
          'input:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) || null
      );
    },

    selectionOverflowElement() {
      return this.$el?.querySelector('.bayhive-autocomplete-overflow-chip') || null;
    },

    selectSingleFilteredOption(event) {
      const picker = this.$refs.autoComplete;
      if (!picker || picker.disabled || !this.fixedOptionsMode || this.remote) return false;

      let option;
      if (this.useMobileSelect) {
        if (!picker.overlayVisible) return false;
        option = picker.singleSelectableSuggestion();
      } else {
        // Tab can arrive before the filter debounce. Resolve from the text the
        // user just typed, rather than the previous rendered suggestions.
        this.fixedOptionsQuery = picker.fixedOptionQuery ?? this.inputElement()?.value ?? '';
        const options = picker.optionGroupLabel
          ? picker.flatOptions(this.autocompleteSuggestions)
          : this.autocompleteSuggestions;
        const matches = options.filter((candidate) => picker.isValidOption(candidate));
        if (matches.length !== 1) return false;
        [option] = matches;
      }
      if (option === null || option === undefined) return false;

      event.preventDefault();
      event.stopPropagation();
      // This also cancels the pending filter and commits through the real picker.
      picker.selectOptionAndClose(event, option);
      return true;
    },

    nextFieldElement() {
      const input = this.inputElement();
      const focusScope = input?.closest('.p-dialog') || document.body;
      const focusableElements = getFocusableElements(focusScope);
      const inputIndex = focusableElements.indexOf(input);
      const overlay = this.overlayElement();

      if (inputIndex === -1) {
        return null;
      }

      return (
        focusableElements
          .slice(inputIndex + 1)
          .find((element) => !this.$el?.contains(element) && !overlay?.contains(element)) || null
      );
    },

    focusNextFieldAfterHidingOverlay(event) {
      const nextField = this.nextFieldElement();

      if (!nextField) {
        return false;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      this.hide(false);

      window.setTimeout(() => {
        const currentNextField = this.nextFieldElement();

        (currentNextField || nextField).focus({ preventScroll: true });
      }, 0);

      return true;
    },

    selectSingleSuggestionOnTab(event, suggestion) {
      const nextField = this.nextFieldElement();

      if (!nextField) {
        return false;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      if (this.singleResultTabSelect) {
        this.singleResultTabSelect(suggestion, event);
        this.$refs.autoComplete?.hide?.(false);
      } else {
        this.$refs.autoComplete?.selectOptionAndClose?.(event, suggestion);
      }

      window.setTimeout(() => {
        const currentNextField = this.nextFieldElement();

        (currentNextField || nextField).focus({ preventScroll: true });
      }, 0);
      return true;
    },

    resetOverlayScrollToStart() {
      this.$nextTick(() => {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            const autocomplete = this.$refs.autoComplete;
            const scrollElement = this.overlayElement()?.querySelector(
              '.p-virtualscroller, .p-autocomplete-list-container, .p-select-list-container',
            );
            const virtualScroller = autocomplete?.virtualScroller;

            // The virtual scroller can be unmounted (e.g. suggestions still
            // empty while a real search request is in flight), in which case
            // its internal `element` ref is unset and scrollToIndex throws.
            if (virtualScroller?.element) {
              virtualScroller.scrollToIndex?.(0, 'auto');
            }

            if (scrollElement) {
              scrollElement.scrollTop = 0;
            }
          });
        });
      });
    },

    submitQueryOnEnter(event) {
      if (
        !this.querySubmitHandler ||
        event.key !== 'Enter' ||
        event.isComposing ||
        event.keyCode === 229 ||
        event.altKey || event.ctrlKey || event.metaKey || event.shiftKey
      ) return false;

      const picker = this.$refs.autoComplete;
      const input = this.useMobileSelect ? picker?.$refs.filterInput?.$el : this.inputElement();
      if (event.target !== input) return false;
      const option = picker?.visibleOptions?.[picker.focusedOptionIndex];
      if (picker?.overlayVisible && (picker.headerKeyboardActive || picker.isValidOption(option))) {
        return false;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      if (event.repeat) return true;

      window.clearTimeout(picker.searchTimeout);
      window.clearTimeout(this.touchSearchTimeout);
      picker.searchTimeout = null;
      this.touchSearchTimeout = null;
      picker.searching = false;
      if (!this.useMobileSelect) picker.pendingInputChange = null;
      this.querySubmitHandler({ originalEvent: event, query: input.value || '' });
      return true;
    },

    handleShellKeydownCapture(event) {
      if (this.submitQueryOnEnter(event)) return;
      if (event.key === 'Escape' || event.code === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.$refs.autoComplete?.finishFixedOptionQuery?.();
        this.hide(false);

        this.$nextTick(() => {
          event.target?.blur?.();
          this.inputElement()?.blur?.();
        });

        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const overflowSelection = this.selectionOverflowElement();

      if (
        !event.shiftKey &&
        event.target === this.inputElement() &&
        !this.tabToOverlay &&
        (this.overlayVisible || overflowSelection)
      ) {
        this.focusNextFieldAfterHidingOverlay(event);
        return;
      }

      if (
        !event.shiftKey &&
        event.target === this.inputElement() &&
        this.overlayVisible &&
        this.$refs.autoComplete?.singleSelectableSuggestion?.() !== null
      ) {
        return;
      }

      if (event.shiftKey) {
        if (!event.target?.closest?.('.bayhive-autocomplete-overflow-chip')) {
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
        this.inputElement()?.focus?.({ preventScroll: true });
        return;
      }

      if (!this.overlayVisible) {
        return;
      }

      if (event.target === this.inputElement() && overflowSelection) {
        event.preventDefault();
        event.stopImmediatePropagation();
        overflowSelection.focus({ preventScroll: true });
        return;
      }

      if (event.target !== this.inputElement()) {
        return;
      }

      const focusTarget = this.firstOverlayFocusTarget();

      if (!focusTarget) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      this.resetOverlayScrollToStart();
      focusTarget.focus({ preventScroll: true });
    },

    showForFocusedInput() {
      if (
        !this.isInputFocused() ||
        (!this.fixedOptionsMode && !this.suggestions.length) ||
        this.overlayVisible
      ) {
        return;
      }

      this.show(true);
    },

    show(...args) {
      // Explicit opens must allow the empty slot to show loading or no results.
      // Parts lookup clears suggestions before opening while its request runs.
      if (!this.isInputFocused() || this.overlayVisible) {
        return;
      }

      this.$refs.autoComplete?.show?.(...args);
    },

    hide(...args) {
      return this.$refs.autoComplete?.hide?.(...args);
    },

    /**
     * Open on search text set from outside, such as a scanned VIN, with its suggestions already
     * loaded. The text stays editable, in the input on desktop and the touch picker's search box.
     */
    showWithQuery(query) {
      const text = String(query ?? '');
      if (this.useMobileSelect) {
        this.pendingTouchQuery = text;
        if (this.overlayVisible) {
          this.$refs.autoComplete.filterValue = text;
          this.fixedOptionsQuery = text;
          this.pendingTouchQuery = null;
        } else {
          this.$refs.autoComplete?.show?.(true);
        }
        return;
      }
      // Entered as if typed: a value set on the input alone is lost on the next re-render.
      const input = this.inputElement();
      if (input) {
        input.focus();
        input.value = text;
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
      this.$refs.autoComplete?.show?.(true);
    },
  },
};
</script>

<template>
  <div
    class="bayhive-autocomplete-shell"
    :class="autocompleteShellClasses"
    @keydown.capture="handleShellKeydownCapture"
  >
    <TouchSelect
      v-if="useMobileSelect"
      ref="autoComplete"
      v-bind="touchSelectAttributes"
      :pt="touchSelectPassThrough"
      :focus-boundary-active="focusBoundaryActive"
      :focus-boundary-elements="focusBoundaryElements"
      :keep-open-on-select="keepOpenOnSelect"
      :single-result-tab-handler="selectSingleSuggestionOnTab"
      :shift-tab-select="shiftTabSelect"
      :tab-exit-handler="tabToOverlay ? null : focusNextFieldAfterHidingOverlay"
      :enter-query-handler="querySubmitHandler ? submitQueryOnEnter : null"
      :virtual-scroller-options="autocompleteVirtualScrollerOptions"
      @before-show="handleTouchBeforeShow"
      @before-hide="handleBeforeHide"
      @blur="handleBlur"
      @filter="handleTouchFilter"
      @focus="handleFocus"
      @hide="handleHide"
      @keydown="$emit('keydown', $event)"
      @show="handleShow"
    >
      <template v-for="slotName in touchSlotNames" #[slotName]="slotProps">
        <slot :name="slotName" v-bind="slotProps || {}" />
      </template>
      <template v-if="$slots.emptyfilter || $slots.empty" #emptyfilter="slotProps">
        <slot v-if="$slots.emptyfilter" name="emptyfilter" v-bind="slotProps || {}" />
        <slot v-else name="empty" v-bind="slotProps || {}" />
      </template>
      <template v-if="$slots.loadingicon || $slots.loader" #loadingicon="slotProps">
        <slot v-if="$slots.loadingicon" name="loadingicon" v-bind="slotProps || {}" />
        <slot v-else name="loader" v-bind="slotProps || {}" />
      </template>
      <template #clearicon="slotProps">
        <slot name="clearicon" v-bind="slotProps">
          <i
            :class="[slotProps.class, 'fa-solid fa-xmark']"
            aria-hidden="true"
            data-pc-section="clearicon"
            @click="slotProps.clearCallback"
          />
        </slot>
      </template>
    </TouchSelect>
    <PrimeAutoComplete
      v-else
      ref="autoComplete"
      v-bind="$attrs"
      :class="autocompleteClasses"
      :complete-on-focus="false"
      :dropdown="dropdown"
      :dropdown-icon="dropdownIcon"
      :dropdown-mode="dropdownMode"
      :focus-on-hover="false"
      :focus-boundary-active="focusBoundaryActive"
      :focus-boundary-elements="focusBoundaryElements"
      :keep-open-on-select="keepOpenOnSelect"
      :options="options"
      :defer-input-change="deferInputChange"
      :option-value="optionValue"
      :option-group-label="autocompleteOptionGroupLabel"
      :option-group-children="autocompleteOptionGroupChildren"
      :empty-search-message="autocompleteEmptyMessage"
      :search-locale="autocompleteSearchLocale"
      :pt="autocompletePassThrough"
      :single-result-tab-handler="selectSingleSuggestionOnTab"
      :shift-tab-select="shiftTabSelect"
      :suggestions="autocompleteSuggestions"
      :virtual-scroller-options="autocompleteVirtualScrollerOptions"
      @before-hide="handleBeforeHide"
      @blur="handleBlur"
      @complete="handleComplete"
      @query-change="$emit('query-change', $event)"
      @focus="handleFocus"
      @hide="handleHide"
      @keydown="handleKeydown"
      @show="handleShow"
    >
      <template v-for="slotName in forwardedSlotNames" #[slotName]="slotProps">
        <slot :name="slotName" v-bind="forwardedSlotProps(slotName, slotProps)" />
      </template>
    </PrimeAutoComplete>

    <span
      v-if="hasSelectedValueSlot"
      class="bayhive-autocomplete-value"
      :class="{ 'bayhive-autocomplete-value--hidden': !showSelectedValueSlot }"
      :style="selectedValueStyle"
      aria-hidden="true"
    >
      <slot name="value" :value="selectedValue" :placeholder="$attrs.placeholder" />
    </span>

    <Chip
      v-if="selectionHasOverflow"
      v-tooltip.top="{
        value: selectionOverflowTooltip,
        class: 'bayhive-autocomplete-overflow-tooltip',
      }"
      class="bayhive-autocomplete-overflow-chip"
      :label="selectionOverflowLabel"
      role="note"
      tabindex="0"
      :aria-label="selectionOverflowAriaLabel"
    />
  </div>
</template>

<style scoped>
.bayhive-autocomplete-shell {
  display: inline-block;
  min-width: 0;
  max-width: 100%;
  position: relative;
}

.bayhive-autocomplete-shell--options {
  --autocomplete-value-padding-x: var(--p-inputtext-padding-x, 0.75rem);
  --autocomplete-value-padding-y: var(--p-inputtext-padding-y, 0.5rem);
  --autocomplete-value-font-size: var(--p-inputtext-font-size, inherit);
}

.bayhive-autocomplete-shell--fluid,
.bayhive-autocomplete-shell--fluid > :deep(.p-autocomplete) {
  width: 100%;
}

.bayhive-autocomplete-shell :deep(.p-autocomplete > .p-autocomplete-overlay.bayhive-field-overlay) {
  inline-size: 100%;
  max-inline-size: 100%;
}

.bayhive-autocomplete--has-selection-overflow :deep(.p-autocomplete-input-multiple) {
  padding-inline-end: 5.5rem;
}

.bayhive-autocomplete-overflow-chip {
  position: absolute;
  inset-block-end: var(--p-autocomplete-padding-y, 0.5rem);
  inset-inline-end: calc(var(--p-autocomplete-dropdown-width, 2.5rem) + 0.25rem);
  z-index: 1;
  cursor: help;
}

.bayhive-autocomplete-shell:has(> .bayhive-autocomplete--selection-expanded)
  .bayhive-autocomplete-overflow-chip {
  inset-block-end: auto;
  inset-block-start: var(--p-autocomplete-padding-y, 0.5rem);
}

.bayhive-autocomplete-shell.bayhive-autocomplete-shell--options.bayhive-autocomplete-shell--custom-value
  :deep(.p-autocomplete-input),
.bayhive-autocomplete-shell.bayhive-autocomplete-shell--options.bayhive-autocomplete-shell--custom-value
  :deep(.p-autocomplete-input::placeholder) {
  color: transparent;
}

.bayhive-autocomplete-shell--options.bayhive-autocomplete-shell--value-slot {
  display: inline-grid;
}

.bayhive-autocomplete-shell--value-slot > :deep(.p-autocomplete),
.bayhive-autocomplete-value {
  grid-area: 1 / 1;
}

/* Preserve custom value dimensions while the input is used for searching. */
.bayhive-autocomplete-value--hidden {
  visibility: hidden;
}

.bayhive-autocomplete-value {
  position: relative;
  padding-block: var(--autocomplete-value-padding-y);
  padding-inline-start: var(--autocomplete-value-padding-x);
  padding-inline-end: calc(
    var(--p-autocomplete-dropdown-width, 2.5rem) + var(--autocomplete-value-padding-x)
  );
  display: flex;
  align-items: center;
  min-width: 0;
  overflow: hidden;
  pointer-events: none;
  color: var(--p-inputtext-color, inherit);
  font-size: var(--autocomplete-value-font-size);
  font-weight: var(--p-inputtext-font-weight, inherit);
}

.bayhive-autocomplete-shell--options:has(.p-autocomplete-clear-icon) .bayhive-autocomplete-value {
  padding-inline-end: calc(
    var(--p-autocomplete-dropdown-width, 2.5rem) + var(--autocomplete-value-padding-x) + 1.5rem
  );
}

.bayhive-autocomplete-shell--options:has(.bayhive-autocomplete--small) {
  --autocomplete-value-padding-x: var(--p-inputtext-sm-padding-x, 0.625rem);
  --autocomplete-value-padding-y: var(--p-inputtext-sm-padding-y, 0.375rem);
  --autocomplete-value-font-size: var(--p-inputtext-sm-font-size, inherit);
}

.bayhive-autocomplete-shell--options:has(.bayhive-autocomplete--large) {
  --autocomplete-value-padding-x: var(--p-inputtext-lg-padding-x, 0.875rem);
  --autocomplete-value-padding-y: var(--p-inputtext-lg-padding-y, 0.625rem);
  --autocomplete-value-font-size: var(--p-inputtext-lg-font-size, inherit);
}

.bayhive-autocomplete-shell--options:has(.p-autocomplete-input:disabled)
  .bayhive-autocomplete-value {
  color: var(--p-inputtext-disabled-color, inherit);
}
</style>
